import React, { useEffect, useState } from 'react';
import styles from './TipPublishPopup.module.scss';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { RootState } from '../../../store';
import { ChannelsState } from '../../../store/channels/reducer';
import { useTranslation } from 'react-i18next';
import { tr } from '../../../utils/translations/translator';
import simpleIcon from '../../../assets/icons/tipPublish/SIMPLE@3x.png';
import combinedIcon from '../../../assets/icons/tipPublish/COMBINED@3x.png';
import { Channel, TipType } from '../interfaces/tip/Tip';
import { channelsActions } from '../../../store/channels/actions';
import { Credentials } from '../../../types/Login';
import { BounceLoader } from 'react-spinners';
import axios from 'axios';
import { getApiAuthorization } from '../../../api/utils';

interface TipPublishPopupProps {
  channelsState: ChannelsState;
  user: Credentials | null;
}

interface OwnedSpace {
  id: string;
  name: string;
  alias: string;
  image_url: string;
  is_challenge: boolean;
}

interface OwnedSpacesResponse {
  owned_spaces: OwnedSpace[];
}

interface OwnedChannel extends Omit<Channel, 'publishable_tip_types'> {
  is_owned: boolean;
  publishable_tip_types: ['FREE'];
  type: 'PUBLIC_CHANNEL';
}

interface DispatchProps {
  getChannels: (tipsterID: string) => any;
}

export const TipPublishPopup: React.FC<
  DispatchProps & TipPublishPopupProps
> = ({ getChannels, channelsState, user }) => {
  const { t } = useTranslation();
  const [selectedChannel, setSelectedChannel] = useState<
    Channel | OwnedChannel | undefined
  >(undefined);
  const [tipType, setTipType] = useState<TipType | undefined>(undefined);
  const [blocked, setBlocked] = useState(false);
  const [ownedChannels, setOwnedChannels] = useState<OwnedChannel[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isChannelSelected, setIsChannelSelected] = useState(false);
  const token = getApiAuthorization();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChannelSelect = (channel: Channel | OwnedChannel) => {
    setTimeout(() => {
      setSelectedChannel(channel);
      setIsChannelSelected(true);
    }, 0);
  };

  const compareDates = (dateString: string | null, hours: number): boolean => {
    if (!dateString || dateString === '0001-01-01T00:00:00Z') {
      return false;
    }
    const date = new Date(dateString);
    const now = new Date();
    const differenceInMilliseconds = now.getTime() - date.getTime();
    const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
    return differenceInHours > hours;
  };

  const fetchData = async () => {
    const url =
      process.env.REACT_APP_ENV === 'dev'
        ? 'https://apibeta.tipsterchat.com/v1/me/relevance'
        : 'https://api.tipsterchat.com/v1/me/relevance';

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // @ts-ignore
      const data = response.tipster_relevance;
      let isBlocked = false;

      // Bloquear si hay un tip abierto que lleva más de X horas sin cerrar
      if (data.old_open_started_at !== '0001-01-01T00:00:00Z') {
        const hoursLimit =
          data.sports_ids[0] === 'bif6kkjp0dp4vp059t10' ? 48 : 8;

        console.log('=== DEBUG TIP PUBLISH LOGIC ===');
        console.log('Sport ID:', data.sports_ids[0]);
        console.log('Special Sport ID (48h):', 'bif6kkjp0dp4vp059t10');
        console.log(
          'Is Special Sport?:',
          data.sports_ids[0] === 'bif6kkjp0dp4vp059t10',
        );
        console.log('Hours Limit:', hoursLimit);
        console.log('old_open_started_at:', data.old_open_started_at);

        const hasPassedTimeLimit = compareDates(
          data.old_open_started_at,
          hoursLimit,
        );
        console.log('Has passed time limit?:', hasPassedTimeLimit);

        // Bloquear si el tip abierto lleva MÁS de X horas sin cerrar
        isBlocked = hasPassedTimeLimit;
        console.log('Final isBlocked:', isBlocked);
        console.log('================================');
      }

      setBlocked(isBlocked);
    } catch (error: any) {
      setError(error);
    }
  };

  const fetchChannelsOwned = async () => {
    try {
      const response: any = await axios.get<OwnedSpacesResponse>(
        'v1/spaces/owned',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const ownedChannels: OwnedChannel[] = response.owned_spaces.map(
        (space: any) => ({
          id: space.id,
          type: space.is_challenge ? 'challenge' : 'space',
          icon_url: space.image_url,
          title: space.name,
          publishable_tip_types: ['FREE'],
          is_owned: true,
        }),
      );

      setOwnedChannels(ownedChannels);
    } catch (error) {
      console.error('Error fetching owned channels:', error);
      setOwnedChannels([]);
    }
  };

  useEffect(() => {
    if (user?.user) {
      getChannels(user.user.id);
      fetchData();
      fetchChannelsOwned();
    }
  }, [user, getChannels]);

  useEffect(() => {
    if (
      !!channelsState.data.length &&
      !channelsState.isLoading &&
      channelsState.requested
    ) {
      const channelIDs = channelsState.data.map((channel) => channel.id);
      const channelIDPreset = channelIDs.find((id) =>
        window.location.href.includes(id),
      );
      if (!channelIDPreset) {
        return;
      }
      const channelPreset: Channel | undefined = channelsState.data.find(
        (channel) => channelIDPreset === channel.id,
      );
      channelPreset && setSelectedChannel(channelPreset);
    }
  }, [channelsState]);

  const handleNavLink = () => {
    if (
      selectedChannel?.type &&
      tipType &&
      selectedChannel.type !== 'challenge'
    ) {
      return (window.location.href = `/betPublish/${tipType.toLowerCase()}/${
        selectedChannel.type
      }`);
    }

    if (
      selectedChannel?.type &&
      tipType &&
      selectedChannel.type === 'challenge'
    ) {
      return (window.location.href = `/betPublish/${tipType.toLowerCase()}/${
        selectedChannel.type
      }/${selectedChannel.id}`);
    }

    return;
  };

  const allChannels = [...channelsState.data, ...ownedChannels];

  return (
    <>
      {channelsState &&
      (channelsState.isLoading ||
        !channelsState.requested ||
        !channelsState.data.length) ? (
        <div className={styles.loader}>
          <BounceLoader color={'#27de81'} size={25} />
          <p>{tr(t, 'loading')} ...</p>
        </div>
      ) : (
        <div className={styles.tipPublishPopup}>
          <div className={styles.wrapper}>
            {blocked && (
              <a href="/my-bets">
                <div className={styles.banner}>
                  <h2>{t('end_to_publish')}</h2>
                  <div>
                    Dirígete <span className={styles.underline}>aquí</span> y
                    finaliza tus pronósticos.
                  </div>
                </div>
              </a>
            )}
            <div
              className={`${styles.container} ${
                blocked ? styles.disabled : ''
              }`}
            >
              <div>
                <div className={styles.header}>
                  1. {tr(t, 'choose_channel')}
                </div>
                <div className={styles.btnWrapper}>
                  {isMobile && isChannelSelected && selectedChannel ? (
                    <div className={styles.selectedFormBtn}>
                      <img
                        src={selectedChannel.icon_url}
                        alt={selectedChannel.title}
                      />
                      <p>{tr(t, selectedChannel.title)}</p>
                      <button
                        className={styles.changeChannelBtn}
                        onClick={() => {
                          setIsChannelSelected(false);
                          setSelectedChannel(undefined);
                        }}
                      >
                        X
                      </button>
                    </div>
                  ) : (
                    allChannels.map((channel) => (
                      <div
                        key={channel.id}
                        className={
                          channel.id === selectedChannel?.id
                            ? styles.selectedFormBtn
                            : styles.formBtn
                        }
                        id={styles[channel.type]}
                        onClick={() => handleChannelSelect(channel)}
                      >
                        <img src={channel.icon_url} alt={channel.title} />
                        <p>{tr(t, channel.title)}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div>
                <div className={styles.header}>
                  2. {tr(t, 'choose_tip_type')}
                </div>
                <div className={styles.btnWrapper}>
                  <div
                    className={
                      tipType === 'SIMPLE'
                        ? styles.selectedFormBtn
                        : styles.formBtn
                    }
                    onClick={() => setTipType('SIMPLE')}
                  >
                    <img src={simpleIcon} alt="bolt icon" />
                    <p>{tr(t, 'simple-tip')}</p>
                  </div>
                  <div
                    className={
                      tipType === 'COMBINED'
                        ? styles.selectedFormBtn
                        : styles.formBtn
                    }
                    onClick={() => setTipType('COMBINED')}
                  >
                    <img src={combinedIcon} alt="bolt icon" />
                    <p>{tr(t, 'combined-tip')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.nextBtnWrapper}>
            <button
              disabled={!tipType || !selectedChannel}
              onClick={() => handleNavLink()}
            >
              {tr(t, 'next')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  channelsState: state.channels,
  user: state.login.user,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getChannels: (tipsterID: string) =>
    dispatch(channelsActions.channelsRequestAction(tipsterID)),
});

export const TipPublishPopupConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TipPublishPopup);
